import { ManuteRequestConfig } from '@/api/types';
import {
    ListRequest, ListQueryParams,
    GroupRequest, RecipientRequest,
} from '@/api/requests/newsletter/mailup/types';

/**
 * Request to find all lists.
 * @param params
 */
export const ALL = (params?: ListQueryParams): ManuteRequestConfig => ({
    method: 'GET',
    url: '/newsletter/mailup/lists',
    params: {
        page_size: 50,
        ...params,
    },
});

/**
 * Request to find a list by id.
 * @param listId
 */
export const FIND = (listId: string | number): ManuteRequestConfig => ({
    method: 'GET',
    url: `/newsletter/mailup/lists/${listId}`,
});

/**
 * Request to save a new list.
 * @param list
 */
export const SAVE = (list: ListRequest): ManuteRequestConfig => ({
    method: 'POST',
    url: `/newsletter/mailup/lists`,
    data: list,
    transformRequestKeysTo: 'PASCAL',
});

/**
 * Request to update an existing list.
 * @param listId
 * @param list
 */
export const UPDATE = (listId: string | number, list: ListRequest): ManuteRequestConfig => ({
    method: 'PUT',
    url: `/newsletter/mailup/lists/${listId}`,
    data: list,
    transformRequestKeysTo: 'PASCAL',
});

/**
 * Request to delete a list.
 * @param listId
 */
export const DESTROY = (listId: string | number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/newsletter/mailup/lists/${listId}`,
});

/**
 * Request to find all groups of a list by its id.
 * @param listId
 */
export const GET_GROUPS = (listId: string | number): ManuteRequestConfig => ({
    method: 'GET',
    url: `/newsletter/mailup/lists/${listId}/groups`,
});

/**
 * Request to find all recipients of a list by its id for status.
 * @param listId
 * @param subscriptionStatus
 */
export const GET_RECIPIENTS = (listId: string | number, subscriptionStatus: string): ManuteRequestConfig => ({
    method: 'GET',
    url: `/newsletter/mailup/lists/${listId}/recipients/${subscriptionStatus}`,
});

/**
 * Request to find all recipients of a list by its id for channel (email or sms).
 * @param listId
 * @param subscriptionChannel
 */
export const GET_RECIPIENTS_SUBSCRIPTIONSTATUS = (
    listId: string | number,
    subscriptionChannel: string): ManuteRequestConfig => ({
    method: 'GET',
    url: `/newsletter/mailup/lists/${listId}/recipients/${subscriptionChannel}`,
});

/**
 * Request to store a recipient in a list.
 * @param listId
 * @param recipient
 * @param confirmEmail
 */
export const STORE_RECIPIENT = (
    listId: string | number, recipient: RecipientRequest,
    confirmEmail: boolean = false): ManuteRequestConfig => ({
    method: 'POST',
    url: `/newsletter/mailup/lists/${listId}/recipients/?confirmEmail=${confirmEmail}`,
    data: recipient,
    transformRequestKeysTo: 'PASCAL',
});

/**
 * Request to destroy a recipient from a list.
 * @param listId
 * @param recipientId
 */
export const DESTROY_RECIPIENT = (listId: string | number, recipientId: string | number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/newsletter/mailup/lists/${listId}/recipients/${recipientId}`,
});

/**
 * Request to subscribe a recipient in a list.
 * @param listId
 * @param recipientId
 */
export const SUBSCRIBE_RECIPIENT = (listId: string | number, recipientId: string | number): ManuteRequestConfig => ({
    method: 'POST',
    url: `/newsletter/mailup/lists/${listId}/recipients/${recipientId}/subscribe`,
});

/**
 * Request to unsubscribe a recipient from a list.
 * @param listId
 * @param recipientId
 */
export const UNSUBSCRIBE_RECIPIENT = (listId: string | number, recipientId: string | number): ManuteRequestConfig => ({
    method: 'DELETE',
    url: `/newsletter/mailup/lists/${listId}/recipients/${recipientId}/unsubscribe`,
});

/**
 * Request to find all countries.
 */
export const GET_COUNTRIES = (): ManuteRequestConfig => ({
    method: 'GET',
    url: '/newsletter/mailup/lists/countries',
});

/**
 * Request to find all time zones.
 */
export const GET_TIMEZONES = (): ManuteRequestConfig => ({
    method: 'GET',
    url: '/newsletter/mailup/lists/timezones',
});
